import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './ComingSoon.css';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import logo from '../../assets/logo.png';
import backgroundVideo from '../../assets/HomeMedia/bgvideo.mp4';

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null)
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const controls = useAnimation();

    // Initialize EmailJS with your public key
  useEffect(() => {
    emailjs.init('NdMBuGB1rXSTIPVvZ');
  }, []);


  // Calculate time until September 25, 2024 12:00 AM PST
  const calculateTimeLeft = () => {
    try {
        // Create the launch date in a consistent UTC format
        const launchDate = new Date('2025-11-01T07:00:00Z'); // Corrected to UTC
        
        // Get the current time in UTC
        const now = new Date();
        const nowUtc = new Date(now.toISOString());
        
        console.log('Launch Date (UTC):', launchDate);
        console.log('Current Date (UTC):', nowUtc);
        
        // Calculate the difference
        const difference = launchDate - nowUtc;
        
        if (difference > 0) {
            return {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            };
        }
        
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        } catch (error) {
            console.error('Error calculating time left:', error);
            return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
    };

  useEffect(() => {
    // Initial calculation
    const initialTimeLeft = calculateTimeLeft();
    console.log('Initial Time Left:', initialTimeLeft);
    setTimeLeft(initialTimeLeft);
    
    // Set up interval
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      console.log('Updated Time Left:', newTimeLeft);
      setTimeLeft(newTimeLeft);
    }, 1000);

    // Clean up interval on unmount
    return () => {
      console.log('Cleaning up timer');
      clearInterval(timer);
    };
  }, []);

  // Format numbers to always show 2 digits
  const formatNumber = (num) => num.toString().padStart(2, '0');

  // Rest of your component...
  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    });
  }, [controls]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!email) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      await emailjs.send(
        'service_8wi9jad', // Service ID
        'template_g58o26j', // Template ID
        {
          email: email,
          signup_date: new Date().toLocaleDateString(),
          site_name: 'Alex Menswear'
        }
      );

      setSubmitted(true);
      setEmail('');
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Email sending failed:', err);
      setError('Failed to submit. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };


    const floatingVariants = {
        float: {
        y: [0, -15, 0],
        transition: {
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
        }
        }
    };

  return (
    <div className="coming-soon-container">
      {/* Background video with fabric texture */}
      <div className="video-background">
        <video autoPlay loop muted playsInline>
          <source src={backgroundVideo} type="video/mp4" />
        </video>
        <div className="video-overlay"></div>
      </div>

      {/* Main content */}
      <motion.div 
        className="content"
        initial={{ opacity: 0 }}
        animate={controls}
      >
        {/* Animated logo */}
        <motion.div
          className="logo-container"
          variants={floatingVariants}
          animate="float"
        >
          <img src={logo} alt="Alex Menswear" className="logo" />
        </motion.div>

        <h1 className="title">
          <span className="title-line">Crafting The Future</span>
          <span className="title-line">Of Menswear</span>
        </h1>

        <p className="subtitle">
          Crafted from our past, designed for your future. Be the first to experience this new era driven by greatness, innovation, and style.
        </p>

        {/* Countdown */}
        <div className="countdown">
          <div className="countdown-item">
            <span className="countdown-number">{formatNumber(timeLeft.days)}</span>
            <span className="countdown-label">Days</span>
          </div>
          <div className="countdown-separator">:</div>
          <div className="countdown-item">
            <span className="countdown-number">{formatNumber(timeLeft.hours)}</span>
            <span className="countdown-label">Hours</span>
          </div>
          <div className="countdown-separator">:</div>
          <div className="countdown-item">
            <span className="countdown-number">{formatNumber(timeLeft.minutes)}</span>
            <span className="countdown-label">Minutes</span>
          </div>
          <div className="countdown-separator">:</div>
          <div className="countdown-item">
            <span className="countdown-number">{formatNumber(timeLeft.seconds)}</span>
            <span className="countdown-label">Seconds</span>
          </div>
        </div>

        {/* Email form */}
         <AnimatePresence>
          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </motion.div>
          )}
          
          {!submitted ? (
            <motion.form 
              className="email-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email for exclusive access"
                required
                disabled={isLoading}
              />
              <button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <span>Get Early Access</span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )}
              </button>
            </motion.form>
          ) : (
            <motion.div 
              className="success-message"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Thank you! We'll be in touch soon.</span>
            </motion.div>
          )}
        </AnimatePresence>

      </motion.div>

      {/* Floating fabric elements */}
      <motion.div 
        className="floating-fabric fabric-1"
        variants={floatingVariants}
        animate="float"
      />
      <motion.div 
        className="floating-fabric fabric-2"
        variants={floatingVariants}
        animate="float"
        style={{ animationDelay: '1s' }}
      />
      <motion.div 
        className="floating-fabric fabric-3"
        variants={floatingVariants}
        animate="float"
        style={{ animationDelay: '2s' }}
      />
    </div>
  );
};

export default ComingSoon;